import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './medarbejder.css';

// Simpel funktion til at konvertere basis markdown til HTML og bevare HTML billeder
const markdownToHtml = (markdown) => {
  if (!markdown) return '';

  // Konverter overskrifter
  let html = markdown.replace(/^## (.*?)$/gm, '<h3>$1</h3>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

  // Konverter links - [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Tilføj centrerings-attributter til billeder som ikke allerede har style
  html = html.replace(/<img(.*?)>/g, (match, attributes) => {
    // Hvis billedet allerede har en style attribut, tilføj centrerings-properties
    if (attributes.includes('style=')) {
      return match.replace(/style="(.*?)"/, 'style="$1; margin-left: auto; margin-right: auto; display: block;"');
    }
    // Ellers tilføj en ny style attribut med centrering
    return `<img${attributes} style="margin-left: auto; margin-right: auto; display: block;">`;
  });

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  html = html.split(/\n/).map(line => {
    if (line.startsWith('<li>')) {
      return line;
    }
    // Bevar linjer der indeholder <img> tags uden at wrappe dem i <p>
    if (line.includes('<img ')) {
      return line;
    }
    return line === '' ? '<br/>' : `<p>${line}</p>`;
  }).join('');

  // Wrap bullets i ul tags
  html = html.replace(/(<li>.*?<\/li>)+/g, '<ul>$&</ul>');

  // Fjern ekstra <br/> tags
  html = html.replace(/<p><br\/><\/p>/g, '<br/>');
  html = html.replace(/<br\/><br\/>/g, '<br/>');

  return html;
};

const Medarbejder = () => {
  const { id } = useParams();
  const [psykolog, setPsykolog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnglish, setIsEnglish] = useState(false);

  useEffect(() => {
    const fetchPsykolog = async () => {
      try {
        const omDocRef = doc(db, 'content', 'om');
        const omDoc = await getDoc(omDocRef);
        
        if (omDoc.exists() && omDoc.data().psykologer) {
          const psykologer = omDoc.data().psykologer;
          const foundPsykolog = psykologer.find(p => p.id === id);
          
          if (foundPsykolog) {
            setPsykolog(foundPsykolog);
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af psykolog:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchPsykolog();
    }
  }, [id]);

  const toggleLanguage = () => {
    setIsEnglish(!isEnglish);
  };

  if (isLoading) {
    return <div className="medarbejder-loading">Indlæser...</div>;
  }

  if (!psykolog) {
    return (
      <div className="medarbejder-not-found">
        <h1>Psykolog ikke fundet</h1>
        <p>Vi kunne ikke finde den psykolog, du leder efter.</p>
        <a href="/om">Tilbage til Om os</a>
      </div>
    );
  }

  const hasEnglishContent = !!psykolog.engBeskrivelse;

  return (
    <div className="medarbejder-side">
      <div className="medarbejder-container">
        <div className="medarbejder-title-container">
          <h1 className="medarbejder-navn">{psykolog.navn}</h1>
          {hasEnglishContent && (
            <button 
              className={`medarbejder-language-toggle ${isEnglish ? 'english' : 'danish'}`}
              onClick={toggleLanguage}
            >
              {isEnglish ? 'Dansk' : 'English'}
            </button>
          )}
        </div>
        
        <div className="medarbejder-header">
          <div className="medarbejder-billede">
            <img src={psykolog.billedeUrl} alt={psykolog.navn} />
          </div>
          
          <div className="medarbejder-info">
            <div className="medarbejder-titler">
              <p>{isEnglish && hasEnglishContent ? (psykolog.engTitler || psykolog.titler) : psykolog.titler}</p>
            </div>
            
            {psykolog.speciale && (
              <div className="medarbejder-speciale">
                <p><strong>{isEnglish && hasEnglishContent ? (psykolog.engSpeciale || psykolog.speciale) : psykolog.speciale}</strong></p>
              </div>
            )}
            
            <div className="medarbejder-kontakt">
              <h2>{isEnglish && hasEnglishContent ? 'Contact' : 'Kontakt'}</h2>
              {isEnglish && hasEnglishContent ? (
                // Engelsk kontaktinfo
                <>
                  <p>{psykolog.engEmail || 'Email:'} {psykolog.email}</p>
                  <p>{psykolog.engTelefon || 'Phone:'} {psykolog.telefon}</p>
                </>
              ) : (
                // Dansk kontaktinfo
                <>
                  <p>Email: {psykolog.email}</p>
                  <p>Tlf: {psykolog.telefon}</p>
                </>
              )}
            </div>
            
            {psykolog.punkter && psykolog.punkter.length > 0 && (
              <div className="medarbejder-punkter">
                <h2>
                  {isEnglish && hasEnglishContent 
                    ? 'Special experience and interest in:' 
                    : 'Særlig erfaring og interesse i:'}
                </h2>
                <ul>
                  {isEnglish && hasEnglishContent && psykolog.engPunkter && psykolog.engPunkter.length > 0
                    ? psykolog.engPunkter.map((punkt, index) => (
                        <li key={index}>{punkt}</li>
                      ))
                    : psykolog.punkter.map((punkt, index) => (
                        <li key={index}>{punkt}</li>
                      ))
                  }
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {(isEnglish && hasEnglishContent) ? (
          <div className="medarbejder-beskrivelse">
            <div className="medarbejder-beskrivelse-content"
                 dangerouslySetInnerHTML={{ __html: markdownToHtml(psykolog.engBeskrivelse) }}>
            </div>
          </div>
        ) : (
          psykolog.langBeskrivelse && (
            <div className="medarbejder-beskrivelse">
              <div className="medarbejder-beskrivelse-content" 
                   dangerouslySetInnerHTML={{ __html: markdownToHtml(psykolog.langBeskrivelse) }}>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Medarbejder;
